// src/components/dashboards/MoneyDashboard.js — FINAL WITH FULL TOTAL
import { useEffect, useState } from "react";
import { client } from "../../sanityClient";
import MoneyForm from "../forms/MoneyForm";
import MoneyTable from "../tables/MoneyTable";
import MoneyFilters from "../filters/MoneyFilters";
import NewUserForm from "../forms/NewUserForm";
import DeleteConfirmModal from "../modals/DeleteConfirmModal";
import Toast from "../common/Toast";
import EditMoneyForm from "../forms/EditMoneyForm";

export default function MoneyDashboard() {
    const [form, setForm] = useState({ givenOrTaken: "", userId: "", amount: "", date: "", remarks: "" });
    const [users, setUsers] = useState([]);
    const [userNames, setUserNames] = useState([]);
    const [showUserModal, setShowUserModal] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [allFilteredData, setAllFilteredData] = useState([]); // ← ALL FILTERED
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [total, setTotal] = useState(0);
    const [filter, setFilter] = useState({ type: "All", user: "All", from: "", to: "" });
    const [sortBy, setSortBy] = useState("created_desc");
    const [deleteModal, setDeleteModal] = useState({ show: false, id: null });
    const [deleteUserModal, setDeleteUserModal] = useState({ show: false, user: null });
    const [toast, setToast] = useState({ show: false, type: "", message: "" });
    const [editingTransaction, setEditingTransaction] = useState(null);
    useEffect(() => { fetchUsers(); fetchUserNames(); }, []);
    useEffect(() => { fetchTransactions(); }, [currentPage, rowsPerPage, filter, sortBy]);

    const fetchUsers = () => client.fetch(`*[_type == "user"]{_id, username}`).then(setUsers);
    const fetchUserNames = () => client.fetch(`*[_type == "moneyTransaction"].user->username`).then(d => setUserNames([...new Set(d.filter(Boolean))]));

    const fetchTransactions = async () => {
        setLoading(true);
        const f = [];
        if (filter.type !== "All") f.push(`givenOrTaken == "${filter.type}"`);
        if (filter.user !== "All") f.push(`user->username == "${filter.user}"`);
        if (filter.from) f.push(`dateTime(date) >= dateTime("${filter.from}T00:00:00Z")`);
        if (filter.to) f.push(`dateTime(date) <= dateTime("${filter.to}T23:59:59Z")`);
        const query = `*[_type == "moneyTransaction"${f.length ? " && " + f.join(" && ") : ""}] | order(_createdAt desc) { 
  _id, MTrID, givenOrTaken, amount, date, remarks, _createdAt, 
  user->{username} 
}`;
        let data = await client.fetch(query);

        // SORTING
        // REPLACE ALL SORTING BLOCK WITH THIS
        if (sortBy === "created_desc") data.sort((a, b) => new Date(b._createdAt) - new Date(a._createdAt));
        if (sortBy === "date_desc") data.sort((a, b) => new Date(b.date) - new Date(a.date));
        if (sortBy === "date_asc") data.sort((a, b) => new Date(a.date) - new Date(b.date));
        if (sortBy === "amount_desc") data.sort((a, b) => b.amount - a.amount);
        if (sortBy === "amount_asc") data.sort((a, b) => a.amount - b.amount);

        setAllFilteredData(data); // ← FULL DATA FOR TOTAL
        setTotal(data.length);
        const start = (currentPage - 1) * rowsPerPage;
        setTransactions(data.slice(start, start + rowsPerPage));
        setLoading(false);
    };

    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false, type: "", message: "" }), 3000);
    };

    const generateMTrID = () => {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, "0");
        const ms = now.getMilliseconds().toString().padStart(3, "0");
        return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}${ms}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.givenOrTaken || !form.userId || !form.amount || !form.date) {
            showToast("error", "Please fill all required fields");
            return;
        }
        if (submitting) return;
        setSubmitting(true);

        try {
            const newID = generateMTrID();
            await client.create({
                _type: "moneyTransaction",
                MTrID: newID,
                givenOrTaken: form.givenOrTaken,
                user: { _type: "reference", _ref: form.userId },
                amount: +form.amount,
                date: new Date(form.date).toISOString(),
                remarks: form.remarks || "",
            });

            setForm({ givenOrTaken: "", userId: "", amount: "", date: "", remarks: "" });
            setCurrentPage(1);
            fetchTransactions();
            showToast("success", `Transaction added! ID: ${newID}`);
        } catch (err) {
            console.error(err);
            showToast("error", "Failed to add transaction");
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = (id) => setDeleteModal({ show: true, id });
    const confirmDelete = async () => {
        await client.delete(deleteModal.id);
        setDeleteModal({ show: false, id: null });
        fetchTransactions();
        showToast("error", "Transaction deleted");  // ← RED TOAST
    };

    const handleEditUser = (user) => {
        const newName = prompt(`Edit username:`, user.username);
        if (newName && newName.trim() && newName.trim() !== user.username) {
            // Check if user with same name already exists
            client.fetch(`*[_type == "user" && lower(username) == $name && _id != $id]`, {
                name: newName.trim().toLowerCase(),
                id: user._id
            }).then(existing => {
                if (existing.length > 0) {
                    showToast("error", "A user with this name already exists");
                } else {
                    client.patch(user._id).set({ username: newName.trim() }).commit()
                        .then(() => {
                            fetchUsers();
                            fetchUserNames();
                            showToast("success", "User updated successfully");
                        })
                        .catch(err => {
                            console.error(err);
                            showToast("error", "Failed to update user");
                        });
                }
            });
        }
    };

    const handleDeleteUser = (user) => {
        setDeleteUserModal({ show: true, user });
    };

    const confirmDeleteUser = async () => {
        const user = deleteUserModal.user;

        // Check if user has any transactions
        const transactions = await client.fetch(`*[_type == "moneyTransaction" && user._ref == $userId]`, {
            userId: user._id
        });

        if (transactions.length > 0) {
            showToast("error", `Cannot delete "${user.username}" because it has ${transactions.length} transaction(s). Delete all transactions first.`);
            setDeleteUserModal({ show: false, user: null });
            return;
        }

        try {
            await client.delete(user._id);
            setDeleteUserModal({ show: false, user: null });
            fetchUsers();
            fetchUserNames();
            showToast("success", "User deleted successfully");
        } catch (err) {
            console.error(err);
            showToast("error", "Failed to delete user");
        }
    };

    // REPLACE handleClear() WITH
    const handleClear = () => {
        setFilter({ type: "All", user: "All", from: "", to: "" });
        setRowsPerPage(25);
        setSortBy("created_desc");  // ← ALWAYS RESET TO TIMESTAMP NEWEST
        setCurrentPage(1);
    };

    return (
        <div className="container-fluid mt-4">
            <MoneyForm
                form={form}
                setForm={setForm}
                users={users}
                onSubmit={handleSubmit}
                onShowUserModal={setShowUserModal}
                submitting={submitting}
                onEditUser={handleEditUser}
                onDeleteUser={handleDeleteUser}
            />

            <MoneyFilters
                filter={filter}
                setFilter={setFilter}
                userNames={userNames}
                onClear={handleClear}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                sortBy={sortBy}
                setSortBy={setSortBy}
            />

            <MoneyTable
                data={transactions}
                allFilteredData={allFilteredData}   // ← FULL DATA FOR TOTAL
                loading={loading}
                currentPage={currentPage}
                totalPages={Math.ceil(total / rowsPerPage)}
                onPageChange={setCurrentPage}
                onDelete={handleDelete}
                onEdit={setEditingTransaction}
            />

            {showUserModal && (
                <div className="modal d-block bg-dark bg-opacity-50" onClick={() => setShowUserModal(false)}>
                    <div className="modal-dialog" onClick={e => e.stopPropagation()}>
                        <div className="modal-content">
                            <NewUserForm
                                onClose={() => setShowUserModal(false)}
                                onUserCreated={() => { fetchUsers(); setShowUserModal(false); }}
                                showToast={showToast}   // ← ADD THIS LINE
                            />
                        </div>
                    </div>
                </div>
            )}

            <DeleteConfirmModal
                show={deleteModal.show}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteModal({ show: false, id: null })}
            />

            <DeleteConfirmModal
                show={deleteUserModal.show}
                onConfirm={confirmDeleteUser}
                onCancel={() => setDeleteUserModal({ show: false, user: null })}
            />

            <Toast
                show={toast.show}
                type={toast.type}
                message={toast.message}
                onClose={() => setToast({ show: false, type: "", message: "" })}
            />

            {editingTransaction && (
                <EditMoneyForm
                    transaction={editingTransaction}
                    onClose={() => setEditingTransaction(null)}
                    onUpdate={() => {
                        setEditingTransaction(null);
                        fetchTransactions();
                    }}
                />
            )}
        </div>
    );
}